import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Select, Skeleton, Table } from "antd";
import { equals, pathOr } from "ramda";
import CustomButton from "../customButton";
import { SearchIcon } from "../../assets/svg";
import "./styles.scss";
import useWindowDimensions from "../custom-hooks/useWindowDimensions";
import { MOBILE_WIDTH } from "../../constants/constants";

const SearchComponent = (props) => {
  const [state, setState] = useState({
    suggestions: [],
    loadingSuggestions: false,
    searchValue: "",
  });

  useEffect(() => {
    onSearch("");
  }, []);

  const searchTimeoutRef = useRef();

  const onSearch = useCallback(
    (value, filterColumn) => {
      setState((st) => ({ ...st, searchValue: value }));
      const updateSuggestions = async () => {
        setState((st) => ({ ...st, loadingSuggestions: true }));
        if (filterColumn || props.filterColumn) {
          const suggestions = await props.getSuggestions?.(
            filterColumn || props.filterColumn,
            value
          );
          const updatedSuggestion = suggestions?.map(
            (suggestion) => suggestion || "Blank"
          );
          let updated = false;
          setState((st) => {
            updated = st.searchValue === value;
            return {
              ...st,
              ...(updated ? { suggestions: updatedSuggestion } : {}),
              loadingSuggestions: !updated,
            };
          });
        }
      };
      if (searchTimeoutRef && searchTimeoutRef.current)
        clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        updateSuggestions();
      }, 600);
    },
    [props.filterColumn]
  );

  const filterColumnName = useMemo(
    () =>
      pathOr(
        "",
        ["title"],
        props.filteredColumns.find((i) =>
          [i.key, i.filter].includes(props.filterColumn)
        )
      ),
    [props.filterColumn]
  );

  const handleFilterColumnChange = (value) => {
    props.changeFilterColumn(value);
    setState((st) => ({
      ...st,
      suggestions: [],
    }));
    onSearch("", value);
  };

  return (
    <>
      <Select
        value={props.filterColumn}
        className="common-select-field filter-column-select"
        getPopupContainer={(trigger) => trigger.parentNode}
        onChange={handleFilterColumnChange}
      >
        {props.filteredColumns.map((column) => (
          <Select.Option value={column.filter || column.key} key={column.key}>
            {column.title}
          </Select.Option>
        ))}
      </Select>

      <Select
        className="filter-column-input common-select-field"
        prefix={SearchIcon}
        placeholder={`Search ${filterColumnName}`}
        value={props.filterValue}
        onChange={props.changeFilterValue}
        onSearch={onSearch}
        showSearch
        loading={state.loadingSuggestions}
        allowClear={!!state.searchValue || props.filterValue}
        clearIcon={<div type="link">Clear</div>}
      >
        {state?.suggestions?.map((item) => {
          return <Select.Option key={item}>{item}</Select.Option>;
        })}
      </Select>
    </>
  );
};

const CommonTable = (props) => {
  const {
    columns,
    loading,
    fetchData,
    title,
    getSuggestions,
    forceUpdate,
    tableData,
    updateTable,
    label,
    selected,
    clearSelection,
    refresh,
    reportHeader = true,
    ...rest
  } = props;
  const [data, setData] = useState({
    rows: [],
    count: 0,
  });
  const { width } = useWindowDimensions();

  useEffect(
    () => {
      if (forceUpdate !== undefined) {
        setData((st) => ({ ...st, rows: [...st.rows] }));
      }
    },
    [forceUpdate]
  );

  useEffect(
    () => {
      if (refresh !== undefined && refresh === true) {
        fetchTableData();
      }
    },
    [refresh]
  );
  useEffect(
    () => {
      if (tableData) {
        setData((st) => ({ ...st, ...tableData }));
      }
    },
    [tableData]
  );

  const filteredColumns = useMemo(
    () => columns.filter((column) => column.filter !== false),
    [columns]
  );

  useEffect(
    () => {
      if (!state.filterColumn) {
        setState((st) => ({
          ...st,
          filterColumn:
            pathOr("", [0, "filter"], filteredColumns) ||
            pathOr("", [0, "key"], filteredColumns),
        }));
      }
    },
    [filteredColumns]
  );

  const [state, setState] = useState({
    filterColumn:
      pathOr("", [0, "filter"], filteredColumns) ||
      pathOr("", [0, "key"], filteredColumns),
    filterValue: undefined,
    pageNo: 1,
    sorter: "",
    loading,
    total: 0,
    search: "",
    pageSize: 15,
  });

  const [searchIconSelected, setSearchIconSelected] = useState(false);
  const toggleSearch = () => {
    setSearchIconSelected((st) => !st);
  };
  useEffect(() => {
    setState((st) => ({ ...st, pageNo: 1 }));
  }, []);

  const changeFilterColumn = (value) => {
    setState((st) => ({
      ...st,
      filterColumn: value,
      filterValue: undefined,
    }));
  };

  const changeFilterValue = (value) => {
    return setState((st) => ({ ...st, filterValue: value }));
  };

  const fetchTableData = async () => {
    let field = state.filterColumn;
    if (field) {
      const column = filteredColumns.find(
        (i) => i.filter === field || i.key === field
      );
      field = column.dataKey || column.key;
    }

    const search =
      field && state.filterValue
        ? { [field]: state.filterValue === "Blank" ? "" : state.filterValue }
        : "";
    try {
      const searchUpdated = !equals(search, state.search);
      setState((st) => ({ ...st, loading: true }));
      setData((st) => ({ ...st, rows: [] }));

      const newTableData = await fetchData({
        page: searchUpdated ? 1 : state.pageNo,
        size: state.pageSize,
        order: state.sorter,
        search,
      });
      setData(newTableData);

      let total = state.total;

      total = newTableData.count;

      setState((st) => ({
        ...st,
        loading: false,
        total,
        pageNo: searchUpdated ? 1 : st.pageNo,
        search,
      }));
    } catch (err) {
      setState((st) => ({
        ...st,
        loading: false,
      }));
    }
  };
  useEffect(
    () => {
      if (fetchData) {
        fetchTableData();
      }
    },
    [
      setData,
      state.pageNo,
      state.sorter,
      state.filterValue,
      state.pageSize,
      updateTable,
    ]
  );

  const locale =
    loading || state.loading
      ? {
          locale: {
            emptyText: (
              <Skeleton
                className="sftp-workflow-skeleton"
                active
                paragraph={{ rows: 10 }}
              />
            ),
          },
        }
      : {};

  const onTableChange = async (pagination, _filters, sorterObj) => {
    const page = pagination && pagination.current && pagination.current;
    let field = sorterObj.field;

    if (field) {
      const column = columns.find((c) => c.key === field);
      field = column.filter || column.key;
    }
    const order = sorterObj.order
      ? sorterObj.order === "ascend"
        ? "asc"
        : "desc"
      : "";

    const sorter = field && order ? `${field}.${order}` : "";

    setState((st) => ({
      ...st,
      pageNo: sorter !== st.sorter ? 1 : page,
      sorter,
      pageSize: pagination.pageSize,
    }));
  };

  return (
    <div className="reports-container" data-testid="common-table-wrapper">
      {reportHeader && (
        <div className="reports-header">
          <div className="reports-heading">
            <div className="reports-heading-data">
              {typeof title === "function" ? (
                <>
                  <span
                    className="reports-heading-description"
                    title={title(state.total)}
                  >
                    {title(state.total)}
                  </span>
                  <p className="heading-content" />
                  <span className="reports-heading-total">
                    {state.total} {label}
                  </span>
                </>
              ) : (
                <>
                  <span className="reports-heading-total">
                    {state.total} {title}
                  </span>
                </>
              )}
            </div>
            <div
              className={`reports-heading-search ${
                searchIconSelected ? "selected" : ""
              }`}
              onClick={toggleSearch}
            >
              <SearchIcon />
            </div>
          </div>

          <div className="search-wrapper desktop-version">
            <SearchComponent
              getSuggestions={getSuggestions}
              changeFilterColumn={changeFilterColumn}
              changeFilterValue={changeFilterValue}
              filterColumn={state.filterColumn}
              filteredColumns={filteredColumns}
              filterValue={state.filterValue}
            />
          </div>
          <div className="search-wrapper mobile-version">
            {searchIconSelected && (
              <SearchComponent
                getSuggestions={getSuggestions}
                changeFilterColumn={changeFilterColumn}
                changeFilterValue={changeFilterValue}
                filterColumn={state.filterColumn}
                filteredColumns={filteredColumns}
                filterValue={state.filterValue}
              />
            )}
          </div>
        </div>
      )}
      {selected > 0 &&
        !loading && (
          <div className="selected-info">
            <div className="info">
              <strong>{selected}</strong> Patients Selected{" "}
              <span className={"notification-icon"} />
              <CustomButton
                btntype="clear"
                className={`ml-8 cancel-button`}
                onClick={clearSelection}
                title={"Clear All"}
              />
            </div>
          </div>
        )}
      <div className="reports-table-container">
        <Table
          {...rest}
          {...locale}
          columns={columns}
          dataSource={data.rows}
          onChange={onTableChange}
          pagination={{
            showTotal: (total, range) => {
              if (width > MOBILE_WIDTH) {
                return `Showing ${range[0]} to ${range[1]} of ${total} results`;
              } else {
                return `${range[0]}-${range[1]}`;
              }
            },
            showSizeChanger: false,
            pageSize: state.pageSize,
            onShowSizeChange: (current, size) => size,
            hideOnSinglePage: true,
            total: data.count,
            current: state.pageNo,
            showLessItems: width < MOBILE_WIDTH ? true : false,
          }}
          scroll={{ y: 431 }}
          className="reports-table"
          forceUpdate={forceUpdate}
        />
      </div>
    </div>
  );
};

export default CommonTable;
